import { useSyncExternalStore } from 'react';
import { API_URL } from 'env';

/**
 * Kết nối tổng đài CareSoft (Voice API) — https://docs.caresoft.vn/danh-muc/tich-hop-thoai/tich-hop-kenh-thoai-tren-ung-dung-web-voice-api
 * Bật bằng REACT_APP_CARESOFT=on. Tắt / chưa lấy được token → softphone chạy chế độ mô phỏng như cũ.
 *
 * Backend: GET /api/v1/be/caresoft/token[?refresh=1] → { status: true, data: { token, domain, ipphone, expired_at } }
 * (BE ký JWT theo máy lẻ của nhân viên đăng nhập, gọi thirdParty/login và cache; refresh=1 bỏ cache, ký phiên mới).
 */
export const CARESOFT_ENABLED = (process.env.REACT_APP_CARESOFT || '').trim() === 'on';

const HOST = 'https://capi.caresoft.vn/js/embed';
const SCRIPTS = ['jquery.min.js', 'jssip-3.2.10.js', 'init-3.0.7.js', 'web.push.js', 'cs_const.js', 'cs_voice.js'];

export type CsMode = 'mock' | 'connecting' | 'ready' | 'error';

export interface CalloutService { callout_id: string; agent_id: string; is_default: number | string; descriptions: string }

export interface CsState {
  mode: CsMode;
  /** Tab này đang giữ quyền thoại (CareSoft chỉ cho 1 tab) */
  voiceHere: boolean;
  /** Trạng thái nhận cuộc gọi do CareSoft báo (csShowCallStatus) */
  callStatus?: string;
  deviceType?: number;
  calloutServices: CalloutService[];
  calloutId?: string;
  /** Máy lẻ CareSoft của nhân viên đang đăng nhập */
  ipphone?: string;
  error?: string;
}

/** Sự kiện cuộc gọi đẩy sang softphone */
export interface CallEvents {
  ringing(phone: string): void;
  calloutStarted(phone: string): void;
  agentAccepted(): void;
  customerAccepted(): void;
  ended(): void;
  calloutError(errorCode: string, sipCode?: string | number): void;
  callId(id: string): void;
  hold(on: boolean): void;
  mute(on: boolean): void;
}

let state: CsState = { mode: 'mock', voiceHere: false, calloutServices: [] };
const listeners = new Set<() => void>();
const update = (patch: Partial<CsState>) => { state = { ...state, ...patch }; listeners.forEach(l => l()); };
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

export const useCareSoft = () => useSyncExternalStore(subscribe, () => state);
export const getCareSoft = () => state;
/** Đang gọi qua tổng đài thật (đã kết nối và tab này giữ quyền thoại) */
export const isLive = () => state.mode === 'ready' && state.voiceHere;

let events: CallEvents | null = null;
export const bindCallEvents = (ev: CallEvents) => { events = ev; };

// Thư viện CareSoft gọi các hàm toàn cục theo tên trên window
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const w = window as any;

/** Lỗi gọi ra (bảng mã lỗi trong tài liệu CareSoft) */
export function calloutErrorText(errorCode: string, sipCode?: string | number): string {
  const sip = String(sipCode ?? '');
  const bySip: Record<string, string> = {
    '486': 'Khách hàng đang bận',
    '408': 'Khách hàng không nghe máy hoặc có lỗi xảy ra',
    '403': 'Lỗi đầu số, liên hệ đơn vị cung cấp đầu số',
    '487': 'Khách hàng không nghe máy',
    '480': 'Khách hàng không nghe máy',
    '404': 'Không kết nối được tới số thuê bao',
    '500': 'Lỗi nhà mạng, liên hệ đơn vị cung cấp đầu số',
    '600': 'Khách không nghe máy hoặc đang bận',
    '603': 'Khách không nghe máy hoặc đang bận',
    '703': 'Số gọi ra bị CareSoft chặn',
    '786': 'Lỗi trạng thái nghe gọi — tắt/bật lại trạng thái hoặc đăng nhập lại',
  };
  const byCode: Record<string, string> = {
    IPCC_NOT_CONNECT_AGENT_ID: 'Không kết nối được thiết bị nghe gọi — kiểm tra IP Phone/Softphone và mạng',
    CALLOUT_AGENT_BUSY: 'Tổng đài báo bận — đăng xuất và đăng nhập lại',
    CALLOUT_PERMISSION_DENY: 'Bạn chưa có quyền gọi ra — liên hệ Admin',
    ERROR_CALLOUT_NUMBER_BLOCKED: 'Số điện thoại bị chặn gọi ra theo cấu hình',
  };
  return byCode[errorCode] || bySip[sip] || 'Gọi ra thất bại';
}

function installCallbacks() {
  w.csCallRinging = (phone: string) => events?.ringing(String(phone || ''));
  w.csAcceptCall = () => events?.agentAccepted();
  w.csCustomerAccept = () => events?.customerAccepted();
  w.csEndCall = () => events?.ended();
  w.showCalloutInfo = (number: string) => events?.calloutStarted(String(number || ''));
  w.showCalloutError = (errorCode: string, sipCode?: string | number) => events?.calloutError(String(errorCode || ''), sipCode);
  w.csCurrentCallId = (callId: string) => events?.callId(String(callId));
  w.csHoldCall = () => events?.hold(true);
  w.csUnHoldCall = () => events?.hold(false);
  w.csMuteCall = () => events?.mute(true);
  w.csUnMuteCall = () => events?.mute(false);

  w.csShowEnableVoice = (isEnable: boolean) => update({ voiceHere: !!isEnable });
  w.csShowCallStatus = (status: string) => update({ callStatus: String(status) });
  w.csShowDeviceType = (type: number) => update({ deviceType: Number(type) });
  w.csInitComplete = () => {
    let services: CalloutService[] = [];
    try { services = w.csVoice?.getCalloutServices?.() || []; } catch { /* bỏ qua */ }
    const def = services.find(s => Number(s.is_default) === 1) || services[0];
    update({ mode: 'ready', error: undefined, calloutServices: services, calloutId: def?.callout_id });
    w.csEnableCall?.(); // giành quyền thoại cho tab vừa mở
  };
  // cs_voice.js báo 500 khi fetch check-partner bị lỗi mạng — thường là CareSoft chưa cho phép tên miền này (CORS)
  w.csInitError = (error: string | number) => update({
    mode: 'error',
    error: String(error) === '500'
      ? `CareSoft chưa cho phép tên miền ${window.location.host} kết nối`
      : `Không kết nối được tổng đài (${error})`,
  });
  w.csNotifyReconnecting = () => update({ error: 'Mất kết nối tổng đài, đang kết nối lại…' });
  w.csOndisconnected = () => update({ mode: 'error', voiceHere: false, error: 'Mất kết nối tổng đài' });

  // Chuyển cuộc gọi / khảo sát: chưa dùng trên giao diện nhưng tài liệu yêu cầu phải khai báo
  w.csListTransferAgent = () => {};
  w.csTransferCallError = () => {};
  w.csTransferCallSuccess = () => {};
  w.csNewCallTransferRequest = () => {};
  w.csTransferCallResponse = () => {};
  w.csTransferSurveyResponse = () => {};
}

function ensureVideos() {
  [['my-video', true], ['peer-video', false]].forEach(([id, muted]) => {
    if (document.getElementById(id as string)) return;
    const v = document.createElement('video');
    v.id = id as string;
    v.autoplay = true;
    v.muted = muted as boolean;
    v.setAttribute('playsinline', '');
    v.style.display = 'none';
    document.body.appendChild(v);
  });
}

const loadScript = (src: string) => new Promise<void>((resolve, reject) => {
  if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
  const s = document.createElement('script');
  s.src = src;
  s.async = false;
  s.onload = () => resolve();
  s.onerror = () => reject(new Error(`Không tải được ${src}`));
  document.head.appendChild(s);
});

async function fetchToken(refresh: boolean): Promise<{ token: string; domain: string; ipphone?: string }> {
  const res = await fetch(`${API_URL}/api/v1/be/caresoft/token${refresh ? '?refresh=1' : ''}`, {
    headers: { Accept: 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.data?.token || !json?.data?.domain) throw new Error(json?.msg || 'Chưa lấy được token tổng đài');
  return json.data;
}

let started = false;

/** Kết nối tổng đài một lần cho cả trang (gọi lại nhiều lần không sao). refresh: xin BE ký phiên mới (nút "Thử lại") */
export async function startCareSoft(refresh = false) {
  if (!CARESOFT_ENABLED || started) return;
  started = true;
  update({ mode: 'connecting', error: undefined });
  try {
    installCallbacks();
    ensureVideos();
    const { token, domain, ipphone } = await fetchToken(refresh);
    update({ ipphone });
    for (const f of SCRIPTS) await loadScript(`${HOST}/${f}`);
    w.csInit(token, domain);
    // Đóng tab / F5: huỷ đăng ký và trả thiết bị nghe gọi về mặc định
    window.addEventListener('pagehide', () => {
      try { w.csUnregister?.(); if (w.csVoice?.enableVoice) w.reConfigDeviceType?.(); } catch { /* bỏ qua */ }
    });
  } catch (e) {
    started = false;
    update({ mode: 'error', error: (e as Error).message });
  }
}

/** Lệnh gửi sang tổng đài */
export const cs = {
  callout(phone: string) { w.csCallout(phone, state.calloutId); },
  accept() { w.onAcceptCall(); },
  end() { w.endCall(); },
  toggleHold() { w.holdCall(); },
  toggleMute() { w.muteCall(); },
  enableHere() { w.csEnableCall(); },
  /** Bật/tắt nhận cuộc gọi (changeCallStatus là công tắc đảo trạng thái) */
  toggleReceiving() { w.changeCallStatus(); },
  setCalloutId(calloutId: string) { update({ calloutId }); },
};
